import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Skeleton } from 'components/ui/skeleton';
import { Textarea } from 'components/ui/textarea';
import { cn } from 'lib/utils';
import type { SoulFieldError } from '../hooks/use-soul-editor';

/** The soul's loading state. */
export function SoulLoading({ testId }: Readonly<{ testId: string }>) {
  return (
    <div className="space-y-3" data-testid={testId}>
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

/** A note or error above the soul, optionally with one action (retry, reload). */
export function SoulAlert({
  message,
  isError = false,
  actionLabel,
  onAction,
}: Readonly<{ message: string; isError?: boolean; actionLabel?: string; onAction?: () => void }>) {
  return (
    <Alert variant={isError ? 'destructive' : 'default'}>
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{message}</span>
        {actionLabel ? (
          <Button type="button" size="sm" variant="outline" onClick={onAction}>
            {actionLabel}
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

type SoulFieldProps = {
  id: string;
  value: string;
  isEditable: boolean;
  /** Buddy's soul is only shown: readOnly, so it stays focusable, scrollable and selectable. */
  isShownOnly?: boolean;
  count: number;
  isTooLong: boolean;
  error: SoulFieldError;
  onChange: (value: string) => void;
};

/** The soul text with its counter (`aria-live`) and the inline too-long / empty message. */
export function SoulField({
  id,
  value,
  isEditable,
  isShownOnly = false,
  count,
  isTooLong,
  error,
  onChange,
}: Readonly<SoulFieldProps>) {
  const { t } = useTranslation();
  const shownError = isEditable ? error : null;
  const errorId = `${id}-error`;
  const errorText =
    shownError === 'tooLong'
      ? t('AGENT_SOUL_TOO_LONG', { defaultValue: 'At most 8000 characters.' })
      : t('AGENT_SOUL_EMPTY', {
          defaultValue:
            'An empty soul can\'t be saved. Use "Restore default" to go back to the default soul.',
        });

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="sr-only">
        {t('AGENT_SOUL_TITLE', { defaultValue: 'Soul' })}
      </Label>
      <div
        className={cn(
          'overflow-hidden rounded-lg border border-border bg-background',
          shownError && 'border-destructive'
        )}
      >
        <Textarea
          id={id}
          rows={12}
          value={value}
          disabled={!isEditable && !isShownOnly}
          readOnly={isShownOnly}
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={shownError !== null}
          aria-describedby={shownError ? errorId : undefined}
          className="min-h-64 resize-y border-0 px-5 py-4 text-sm leading-6 shadow-none focus-visible:ring-0"
        />
        {isEditable ? (
          <div
            aria-live="polite"
            className={cn(
              'flex items-center justify-end border-t border-border bg-muted/20 px-4 py-2 text-sm text-muted-foreground',
              isTooLong && 'text-destructive'
            )}
          >
            {t('AGENT_SOUL_COUNTER', { count, defaultValue: '{{count}} / 8000 characters' })}
          </div>
        ) : null}
      </div>
      {shownError ? (
        <p id={errorId} className="text-sm text-destructive">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

type SoulActionsProps = {
  canReset: boolean;
  canSave: boolean;
  onReset: () => void;
  onSave: () => void;
};

/** [Standard wiederherstellen] and [Speichern]. */
export function SoulActions({ canReset, canSave, onReset, onSave }: Readonly<SoulActionsProps>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <Button type="button" variant="outline" disabled={!canReset} onClick={onReset}>
        {t('AGENT_SOUL_RESET', { defaultValue: 'Restore default' })}
      </Button>
      <Button type="button" disabled={!canSave} onClick={onSave}>
        {t('AGENT_SOUL_SAVE', { defaultValue: 'Save' })}
      </Button>
    </div>
  );
}
